import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Icon from "theme/components/atoms/Icon";
import deprecateRenamedProperty from "web/core/dx/deprecateRenamedProperty";

const mapping = {
  success: "checkmark-circle",
  error: "cross-circle",
  info: "chevron-right-circle",
  warn: "warn",
};

const appearanceToIcon = (appearance) => {
  if (!Object.prototype.hasOwnProperty.call(mapping, appearance)) {
    console.warn(`No icon referenced in 'Alert' component for '${appearance}'`);
    return;
  }

  return mapping[appearance];
};

const Alert = ({
  type,
  appearance,
  position = "fixed",
  children,
  autoHide = 8000,
}) => {
  if (!appearance) {
    appearance = deprecateRenamedProperty("Alert", "type", "appearance", type);
  }

  const [isVisible, setIsVisible] = useState(
    position === "static" ? true : false
  );
  const [isLeaving, setIsLeaving] = useState(false);
  const [timerBarWidth, setTimerBarWidth] = useState(0);

  useEffect(() => {
    // Animation d'entrée seulement si position n'est pas static
    if (position !== "static") {
      const enterTimer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });

      return () => cancelAnimationFrame(enterTimer);
    }
  }, [position]);

  useEffect(() => {
    if (autoHide && isVisible && position !== "static") {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const progress = (elapsedTime / autoHide) * 100;

        if (progress >= 100) {
          clearInterval(interval);
          setIsLeaving(true);
          setTimeout(() => {
            setIsVisible(false);
          }, 300);
        } else {
          setTimerBarWidth(progress);
        }
      }, 16);

      return () => clearInterval(interval);
    }
  }, [autoHide, isVisible, position]);

  return (
    <div
      className={classNames(
        "alert",
        `alert--${appearance}`,
        `alert--${position}`,
        {
          "alert--visible": isVisible,
          "alert--leaving": isLeaving,
        }
      )}
    >
      <div className="alert__content">
        <Icon icon={appearanceToIcon(appearance)} title="" />
        <div className="alert__content__message">{children}</div>
      </div>
      <div
        className="alert__timer-bar"
        style={{ width: `${timerBarWidth}%` }}
      ></div>
    </div>
  );
};

Alert.propTypes = {
  type: PropTypes.oneOf(["success", "error", "info", "warn"]),
  appearance: PropTypes.oneOf(["success", "error", "info", "warn"]).isRequired,
  children: PropTypes.node,
  position: PropTypes.oneOf(["fixed", "static"]),
};

export default Alert;
